import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map';
import { booleanConverter } from './common/converters';
import { TeComponent } from './common/te-component';
import { checkAndUpdateParentPosition, createBodyAnchor } from './common/utils';

const anchorName = 'te-overlay-anchor';

/**
 * Overlay Component
 *
 * @slot - Default slot for overlay content
 */
@customElement('te-overlay')
export class TeOverlay extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-overlay {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ];

  constructor() {
    super();
    createBodyAnchor(anchorName);
  }

  /** Defines the shading type of the overlay */
  @property({ type: String }) variant: 'dark' | 'neutral' | 'light' = 'light';
  /** Determines if the overlay should be contained inside it's parent */
  @property({ type: Boolean, converter: booleanConverter }) contained = false;
  /** Distance from left */
  @property({ type: Number }) marginLeft = 0;
  /** Distance from right */
  @property({ type: Number }) marginRight = 0;
  /** Distance from top */
  @property({ type: Number }) marginTop = 0;
  /** Distance from bottom */
  @property({ type: Number }) marginBottom = 0;

  firstUpdated() {
    if (this.contained) {
      // Parent must have proper positioning set for correct rendering
      checkAndUpdateParentPosition(this.parentElement);
    } else {
      const anchor = document.getElementById(anchorName);
      anchor && anchor.appendChild(this);
    }
  }

  getStyle() {
    const baseStyling = {
      left: '0',
      top: '0',
      marginLeft: this.marginLeft + 'px',
      marginRight: this.marginRight + 'px',
      marginTop: this.marginTop + 'px',
      marginBottom: this.marginBottom + 'px',
      width: `calc(100${this.contained ? '%' : 'vw'} - ${this.marginLeft + this.marginRight}px)`,
      height: `calc(100${this.contained ? '%' : 'vh'} - ${this.marginTop + this.marginBottom}px)`,
    };

    const backdropFilterMap = {
      dark: 'blur(2px) brightness(0.5)',
      neutral: 'blur(4px)',
      light: 'blur(4px) brightness(1.2)',
    };

    if ('backdropFilter' in document.body.style)
      return {
        ...baseStyling,
        backdropFilter: backdropFilterMap[this.variant],
        '-webkit-backdropFilter': backdropFilterMap[this.variant],
      };

    // Specially for Firefox, in case layout.css.backdrop-filter.enabled is not configured to true.
    const backgroundFilterMap = {
      dark: 'black',
      neutral: 'gray',
      light: 'lightGray',
    };

    return { ...baseStyling, background: backgroundFilterMap[this.variant], opacity: '50%' };
  }

  render() {
    return html`
      <div class="te-overlay" style=${styleMap(this.getStyle())}>
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-overlay': TeOverlay;
  }
}
